import React, { useEffect, useState } from "react";
import {
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Inline-edit grid over the filtered term list: values save on blur when
// changed, mirroring the old Custom Captions table idiom.
const BulkEditRow = ({ term, readOnly, saving, onSaveRow }) => {
	const { t } = useTranslation();
	const [singular, setSingular] = useState(term.effectiveSingular);
	const [plural, setPlural] = useState(term.effectivePlural);

	useEffect(() => {
		setSingular(term.effectiveSingular);
		setPlural(term.effectivePlural);
	}, [term.key, term.effectiveSingular, term.effectivePlural]);

	const maxLength = term.key.startsWith("entity.") ? 50 : undefined;

	const commit = () => {
		const values = { singular: singular.trim(), plural: plural.trim() };
		const dirty =
			values.singular !== term.effectiveSingular ||
			(term.hasPlural && values.plural !== term.effectivePlural);
		const complete =
			values.singular !== "" && (!term.hasPlural || values.plural !== "");

		if (dirty && complete) {
			onSaveRow(term, values);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			e.target.blur();
		}
	};

	return (
		<TableRow hover>
			<TableCell sx={{ whiteSpace: "nowrap" }}>
				{term.label}
				{term.isOverridden && (
					<Chip
						label={t("label.overridden")}
						size="small"
						color="primary"
						sx={{ ml: 1 }}
					/>
				)}
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					fullWidth
					value={singular}
					onChange={(e) => setSingular(e.target.value)}
					onBlur={commit}
					onKeyDown={handleKeyDown}
					disabled={readOnly || saving}
					inputProps={{
						maxLength,
						"aria-label": `${term.key} singular`,
					}}
				/>
			</TableCell>
			<TableCell>
				{term.hasPlural ? (
					<TextField
						size="small"
						fullWidth
						value={plural}
						onChange={(e) => setPlural(e.target.value)}
						onBlur={commit}
						onKeyDown={handleKeyDown}
						disabled={readOnly || saving}
						inputProps={{
							maxLength,
							"aria-label": `${term.key} plural`,
						}}
					/>
				) : (
					"—"
				)}
			</TableCell>
		</TableRow>
	);
};

const BulkEditGrid = ({ terms, readOnly, savingKeys, onSaveRow }) => {
	const { t } = useTranslation();

	return (
		<TableContainer>
			<Table size="small" aria-label="Bulk edit terminology">
				<TableHead>
					<TableRow>
						<TableCell>{t("label.term")}</TableCell>
						<TableCell>{t("label.singular")}</TableCell>
						<TableCell>{t("label.plural")}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{terms.map((term) => (
						<BulkEditRow
							key={term.key}
							term={term}
							readOnly={readOnly}
							saving={savingKeys.has(term.key)}
							onSaveRow={onSaveRow}
						/>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default BulkEditGrid;
